import {
    Button,
    Combobox,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogOpenChangeData,
    DialogOpenChangeEvent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Label,
    tokens,
    useId,
} from '@fluentui/react-components';
import { DialogOpenChangeEventHandler } from '@fluentui/react-dialog';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { getComponent, NumberFormatOptions } from '@syncfusion/ej2-base';
import {
    Aggregate,
    AggregateColumnDirective,
    AggregateColumnsDirective,
    AggregateDirective,
    AggregatesDirective,
    ColumnDirective,
    ColumnsDirective,
    Edit,
    EditSettingsModel,
    GridComponent,
    IEditCell,
    Inject,
} from '@syncfusion/ej2-react-grids';
import { NumericTextBox, NumericTextBoxComponent, NumericTextBoxModel } from '@syncfusion/ej2-react-inputs';
import { Dispatch, FC, KeyboardEventHandler, RefObject, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAdjustmentAccountAssociations, useSaveAmounts } from '../../api/report/report';
import { GridStateContextProvider } from '../../contexts/gridStateContext';
import { useEnterAmountsDialogStyles } from '../../hooks/useEnterAmountsDialogStyles';
import { useEnterAmountsGridComponentProps } from '../../hooks/useEnterAmountsGridComponentProps';
import {
    AdjustmentAccountAndAmountAssociationResponse,
    AdjustmentAccountAndAmountAssociationsResponse,
    ExtractionDetailsAccountSummary,
    ProblemDetails,
    SaveAmountsRequest,
    SaveAmountsResponse,
    WhitePaperReportChanges,
} from '../../model';
import { AdjustmentAccountAndAmountAssociationRequest } from '../../model/adjustmentAccountAndAmountAssociationRequest';
import { EnterAmountsDialogReducerActionParams, EnterAmountsModalAction } from '../../reducers/enterAmountsDialogReducer';
import { getChangeTrackingData } from '../../utils/ChangeTrackingDataUtils';
import { deFormatNumber, parseNumericValue } from '../../utils/NumberUtils';
import { mergeStylesOnDemand } from '../../utils/StyleUtils';
import { ErrorType } from '../../utils/UseCustomInstance';
import LoadingSpinner from '../common/LoadingSpinner';

export interface IEnterAmountsDialogProps {
    show: boolean;
    adjustmentCode: string;
    case: string;
    jurisdiction: string;
    entity: string;
    period: string;
    location: string;
    rowNumber?: string;
    columnId?: number;
    changeTrackingData?: WhitePaperReportChanges;
    taxReturnKey: number;
    isLoading: boolean;
    saveButtonIsDisabled: boolean;
    rowDescription: string;
    updateState: Dispatch<EnterAmountsDialogReducerActionParams>;
    updateChangeTrackingData?: (changeTrackingData: WhitePaperReportChanges) => void;
    buttonTrigger?: HTMLElement;
}

export interface IAccountAmountRow extends AdjustmentAccountAndAmountAssociationResponse {
    debitTotal?: number;
    creditTotal?: number;
}

export function getUpdatedAccountAmountRow(
    originalRow: IAccountAmountRow,
    batchChanges: { changedRecords: IAccountAmountRow[] }
): IAccountAmountRow | undefined {
    const updatedRow: IAccountAmountRow | undefined = batchChanges.changedRecords.find(
        (changedRecord) => changedRecord.accountCode === originalRow.accountCode
    );
    return updatedRow;
}

const EnterAmountsDialog: FC<IEnterAmountsDialogProps> = (props: IEnterAmountsDialogProps) => {
    const { t } = useTranslation();
    const gridRef: RefObject<GridComponent> = useRef<GridComponent>(null);
    const { onCellSave, onGridCreated, onCellEdit, onCellSaved, onRowDataBound, onGridDataBound, onKeyPressed } =
        useEnterAmountsGridComponentProps(gridRef, props.updateState, props.saveButtonIsDisabled);
    const {
        modalStyles,
        dialogContentStyles,
        gridComponentCSSClass,
        totalRowStyle,
        spinnerClassName,
        buttonOverrides,
        dialogBodyStyles,
        comboboxStyles,
    } = useEnterAmountsDialogStyles();
    const { mutate: getAdjustmentAccountAndAmountAssociations } = useGetAdjustmentAccountAssociations();
    const { mutate: saveAmounts } = useSaveAmounts();
    const closeLabel = useMemo(() => t('close'), [t]);
    const cancelLabel = useMemo(() => t('cancel'), [t]);
    const confirmLabel = useMemo(() => t('save'), [t]);
    const dialogHeading = useMemo(() => `${t('quickAddHeader')} ${props.adjustmentCode}`, [props.adjustmentCode, t]);

    const primaryHandler = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            function retrieveNormalizedRowValue(row: IAccountAmountRow): string {
                let amount: string = '0';

                if (row.accountBalanceType === 'Debit') {
                    if (row.debitTotal) {
                        amount = row.debitTotal.toString();
                    } else if (row.creditTotal) {
                        amount = (row.creditTotal * -1).toString();
                    }
                } else if (row.accountBalanceType === 'Credit') {
                    if (row.creditTotal) {
                        amount = row.creditTotal.toString();
                    } else if (row.debitTotal) {
                        amount = (row.debitTotal * -1).toString();
                    }
                } else {
                    throw new Error(`Invalid account balance type: ${row.accountBalanceType}`);
                }

                return amount;
            }

            function convertIAccountAmountRowToExtractionDetailsAccountSummary(row: IAccountAmountRow): ExtractionDetailsAccountSummary {
                let amount: string = retrieveNormalizedRowValue(row);

                return {
                    accountNumber: row.accountCode,
                    adjustmentInfo: [
                        {
                            adjustmentCode: props.adjustmentCode,
                            case: props.case,
                            corporation: props.entity,
                            jurisdiction: props.jurisdiction,
                            location: props.location,
                            reportAmount: amount,
                            year: props.period,
                        },
                    ],
                    reportAmount: amount,
                };
            }

            function createSaveAmountsRequestIfRowsChanged() {
                const updatedRows: IAccountAmountRow[] = findUpdatedRows();

                if (updatedRows.length === 0) {
                    // Nothing to save.
                    return null;
                }

                const accountSummaryUpdates = updatedRows.map(convertIAccountAmountRowToExtractionDetailsAccountSummary);
                const saveAmountsRequest: SaveAmountsRequest = {
                    accountSummaryUpdates: accountSummaryUpdates,
                    changesTrackingData: getChangeTrackingData(
                        props.rowNumber!,
                        props.columnId!,
                        props.changeTrackingData!,
                        accountSummaryUpdates
                    ),
                    taxReturnKey: props.taxReturnKey,
                };

                return saveAmountsRequest;
            }

            function findUpdatedRows(): IAccountAmountRow[] {
                gridRef!.current!.editModule.batchSave();
                gridRef!.current!.refresh();

                const batchChanges: { changedRecords: IAccountAmountRow[] } = gridRef.current!.getBatchChanges() as {
                    changedRecords: IAccountAmountRow[];
                };
                const rowElements = (gridRef!.current!.getContentTable() as HTMLTableElement).rows;

                // https://www.syncfusion.com/forums/167379/getting-access-to-changed-rows-in-a-grid-in-batch-edit-mode?reply=SEg2UC
                const updatedRows: IAccountAmountRow[] = [];

                for (const rowElement of rowElements) {
                    const updatedRow: IAccountAmountRow | undefined = getUpdatedAccountAmountRow(
                        gridRef.current!.getRowInfo(rowElement)?.rowData as IAccountAmountRow,
                        batchChanges
                    );

                    if (updatedRow) {
                        updatedRows.push(updatedRow);
                    }
                }

                return updatedRows;
            }

            const saveAmountsRequest: SaveAmountsRequest | null = createSaveAmountsRequestIfRowsChanged();

            if (saveAmountsRequest) {
                saveAmounts(
                    {
                        data: saveAmountsRequest,
                    },
                    {
                        onSuccess: (
                            data: SaveAmountsResponse | undefined,
                            variables: { data: SaveAmountsRequest },
                            context: unknown | undefined
                        ) => {
                            props.updateState({
                                type: EnterAmountsModalAction.ModalClose,
                                changeTrackingData: variables.data.changesTrackingData,
                                updateChangeTrackingData: props.updateChangeTrackingData,
                            });
                        },
                    }
                );
            }
        },
        [props.updateState, props.updateChangeTrackingData, gridRef, gridRef.current, saveAmounts]
    );

    const [accountAmountRows, setAccountAmountRows] = useState<IAccountAmountRow[] | undefined>();

    const request: AdjustmentAccountAndAmountAssociationRequest = {
        caseCode: props.case,
        location: props.location,
        entityCode: props.entity,
        period: props.period,
        adjustmentCode: props.adjustmentCode,
        jurisdiction: props.jurisdiction,
    };

    useLayoutEffect(
        () =>
            getAdjustmentAccountAndAmountAssociations(
                {
                    data: request,
                },
                {
                    onSettled: (
                        data: AdjustmentAccountAndAmountAssociationsResponse | undefined,
                        error: ErrorType<ProblemDetails> | null,
                        variables: { data: AdjustmentAccountAndAmountAssociationRequest },
                        context: unknown | undefined
                    ) => {
                        props.updateState({ type: EnterAmountsModalAction.AssociationsFetchSuccess });
                    },
                    onSuccess: (
                        data: AdjustmentAccountAndAmountAssociationsResponse | undefined,
                        variables: { data: AdjustmentAccountAndAmountAssociationRequest },
                        context: unknown | undefined
                    ) => {
                        const accountAmountRows: IAccountAmountRow[] | undefined = data?.accounts?.map(
                            (account: AdjustmentAccountAndAmountAssociationResponse) => {
                                return {
                                    ...account,
                                    debitTotal: account.debitAmount ? parseNumericValue(account.debitAmount) : undefined,
                                    creditTotal: account.creditAmount ? parseNumericValue(account.creditAmount) : undefined,
                                } as IAccountAmountRow;
                            }
                        );
                        setAccountAmountRows(accountAmountRows);
                    },
                }
            ),
        []
    );

    const editSettings: EditSettingsModel = {
        allowEditing: true,
        allowAdding: false,
        allowDeleting: false,
        showConfirmDialog: false,
        mode: 'Batch',
    };

    const numericEditParams: IEditCell = {
        params: {
            format: 'N',
            decimals: 0,
            showSpinButton: false,
            htmlAttributes: { maxlength: '15' },
            pasteHandler(e: ClipboardEvent) {
                // _this.setElementValue doesnt work when object is declared as NumericTextBox because functions are marked as private.
                //This code was given to us by Syncfusion in order to intercept paste values and change them before completing the paste event.
                const textBoxComponent = getComponent<NumericTextBoxComponent>(e.target! as HTMLElement, NumericTextBox) as any;
                const defaultValue = '';
                if (!textBoxComponent.enabled || textBoxComponent.readonly) {
                    return;
                }
                setTimeout(function () {
                    const number = deFormatNumber(textBoxComponent.element.value).trim();
                    textBoxComponent.setElementValue(number);
                    if (!textBoxComponent.numericRegex().test(textBoxComponent.element.value)) {
                        textBoxComponent.setElementValue(defaultValue);
                    }
                });
            },
            // this overrides the default keydown handler for the NumericTextBox, which increments and decrements the value by 1 on arrow up/down respectively
            // instead, we want to persist the value currently in the cell, stop editing, and navigate to the next cell
            keyDownHandler(e: KeyboardEvent) {
                if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
                    // all of this code comes directly from the Syncfusion NumericTextBox source code, which
                    // is why it is using deprecated function calls
                    var evt = document.createEvent('HTMLEvents');
                    evt.initEvent('change', false, true);
                    const textBoxComponent = getComponent<NumericTextBoxComponent>(e.target! as HTMLElement, NumericTextBox) as any;
                    textBoxComponent.element.dispatchEvent(evt);
                }
            },
            // this is the only way we are able to intercept the focusOut event on the NumericTextBox when a user hits the tab key
            // without this, the textbox will not go away, and keyboard focus will move outside of the grid
            focusOutHandler(e: FocusEvent) {
                gridRef?.current?.editModule?.saveCell();
            },
        } as NumericTextBoxModel,
    };

    const numberFormatOptions = {
        format: 'N',
        useGrouping: true,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    } as NumberFormatOptions;

    const totalRowTemplate = useCallback(
        (props: any) => {
            const className = mergeStylesOnDemand(totalRowStyle)();
            return <span className={className}>{t('total')}</span>;
        },
        [mergeStylesOnDemand, t, totalRowStyle]
    );

    const caseLabelId: string = useId('case');
    const jurisdictionLabelId: string = useId('jurisdiction');
    const entityLabelId: string = useId('entity');
    const periodLabelId: string = useId('period');
    const adjustmentLabelId: string = useId('adjustment');

    const onDialogOpenChange: DialogOpenChangeEventHandler = (event: DialogOpenChangeEvent, data: DialogOpenChangeData) => {
        if (!data.open) {
            if (data.type === 'escapeKeyDown') {
                event.preventDefault();
                event.stopPropagation();
            }
            if (props.buttonTrigger) {
                props.buttonTrigger.focus();
            }
            props.updateState({ type: EnterAmountsModalAction.ModalClose });
        }
    };

    const cancelButtonRef: RefObject<HTMLButtonElement> = useRef(null);
    /*
        This function serves as a way to work around an issue where if a user hits the Escape key while focused anywhere inside a syncfusion grid,
        syncfusion will call preventDefault() on that keydown event. 

        The FocusTrapZone in Fluent 8 does not care about this fact, but tabster (the library that Fluent 9 uses for keyboard navigation and focus management)
        will not fire the onOpenChange event if the native event has defaultPrevented === true.

        As a result of this, the FocusTrapZone that is still used by the ReportDrillDown component will receive the Escape keydown event,
        which will close the entire drilldown panel instead of just the EnterAmountsDialog.
    */
    const syncfusionFocusHack: KeyboardEventHandler<HTMLDivElement> = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const eventTarget: HTMLElement = event.target as HTMLElement;
        if (event.key === 'Escape' && eventTarget.closest('.e-grid')) {
            event.stopPropagation();
            cancelButtonRef?.current?.click();
        }
    };

    return (
        <Dialog open={props.show} onOpenChange={onDialogOpenChange}>
            <DialogSurface className={modalStyles} aria-modal='false' onKeyDown={syncfusionFocusHack}>
                <DialogBody className={dialogBodyStyles}>
                    <DialogTitle
                        action={
                            <DialogTrigger action='close'>
                                <Button ref={cancelButtonRef} appearance='subtle' aria-label={closeLabel} icon={<Dismiss24Regular />} />
                            </DialogTrigger>
                        }
                    >
                        {dialogHeading}
                        <div style={{ fontFamily: 'Segoe UI', fontSize: 14, fontStyle: 'normal', fontWeight: 400 }}>
                            {props.rowDescription}
                        </div>
                    </DialogTitle>
                    <DialogContent className={dialogContentStyles}>
                        {!props.isLoading && (
                            <div
                                style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '24px', height: 'inherit' }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                        height: 'inherit',
                                    }}
                                >
                                    <div>
                                        <Label id={periodLabelId} weight='semibold'>
                                            {t('period')}
                                        </Label>
                                        <Combobox
                                            className={comboboxStyles}
                                            aria-labelledby={periodLabelId}
                                            placeholder={props.period}
                                            disabled={true}
                                        />
                                    </div>

                                    <div>
                                        <Label id={caseLabelId} weight='semibold'>
                                            {t('case')}
                                        </Label>
                                        <Combobox
                                            className={comboboxStyles}
                                            aria-labelledby={caseLabelId}
                                            placeholder={props.case}
                                            disabled={true}
                                        />
                                    </div>
                                    <div>
                                        <Label id={entityLabelId} weight='semibold'>
                                            {t('entity')}
                                        </Label>
                                        <Combobox
                                            className={comboboxStyles}
                                            aria-labelledby={entityLabelId}
                                            placeholder={props.entity}
                                            disabled={true}
                                        />
                                    </div>
                                    <div>
                                        <Label id={jurisdictionLabelId} weight='semibold'>
                                            {t('jurisdiction')}
                                        </Label>
                                        <Combobox
                                            className={comboboxStyles}
                                            aria-labelledby={jurisdictionLabelId}
                                            placeholder={props.jurisdiction}
                                            disabled={true}
                                        />
                                    </div>
                                    <div>
                                        <Label id={adjustmentLabelId} weight='semibold'>
                                            {t('adjustment')}
                                        </Label>
                                        <Combobox
                                            aria-labelledby={adjustmentLabelId}
                                            placeholder='Select an adjustment code'
                                            disabled={true}
                                            className={comboboxStyles}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <svg xmlns='http://www.w3.org/2000/svg' width='7' height='412' viewBox='0 0 7 412' fill='none'>
                                        <path
                                            d='M1.00002 -2.18556e-07L1.00001 186.736C1.00001 187.058 1.07786 187.375 1.22692 187.661L5.54771 195.941C5.8331 196.488 5.85015 197.136 5.59393 197.697L1.1807 207.362C1.06163 207.623 1.00001 207.906 1.00001 208.193L1 412'
                                            stroke={tokens.colorNeutralStroke1}
                                        />
                                    </svg>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                        height: 'inherit',
                                    }}
                                >
                                    <GridStateContextProvider grid={gridRef}>
                                        <GridComponent
                                            className={gridComponentCSSClass}
                                            dataSource={accountAmountRows}
                                            gridLines='Both'
                                            editSettings={editSettings}
                                            cellEdit={onCellEdit}
                                            ref={gridRef}
                                            cellSave={onCellSave}
                                            cellSaved={onCellSaved}
                                            rowDataBound={onRowDataBound}
                                            created={onGridCreated}
                                            keyPressed={onKeyPressed}
                                            rowHeight={28}
                                            dataBound={onGridDataBound}
                                        >
                                            <ColumnsDirective>
                                                <ColumnDirective
                                                    field='accountCode'
                                                    headerText={t('account').toString()}
                                                    textAlign='Left'
                                                    type='string'
                                                    width='102'
                                                    allowEditing={false}
                                                    // if one of the columns is not designated as the primary key, aggregation will not work.
                                                    // without this, the sum will be calculated as the amount in the first cell multiplied
                                                    // by the number of rows in the table
                                                    isPrimaryKey={true}
                                                />
                                                <ColumnDirective
                                                    field='accountDescription'
                                                    headerText={t('description').toString()}
                                                    textAlign='Left'
                                                    type='string'
                                                    width='206'
                                                    allowEditing={false}
                                                />
                                                <ColumnDirective
                                                    field='accountBalanceType'
                                                    headerText={t('type').toString()}
                                                    textAlign='Center'
                                                    type='string'
                                                    width='61'
                                                    allowEditing={false}
                                                />
                                                <ColumnDirective
                                                    field='debitTotal'
                                                    headerText={t('debit').toString()}
                                                    textAlign='Right'
                                                    width='140'
                                                    format={numberFormatOptions}
                                                    edit={numericEditParams}
                                                    editType='numericedit'
                                                />
                                                <ColumnDirective
                                                    field='creditTotal'
                                                    headerText={t('credit').toString()}
                                                    textAlign='Right'
                                                    width='140'
                                                    format={numberFormatOptions}
                                                    edit={numericEditParams}
                                                    editType='numericedit'
                                                />
                                            </ColumnsDirective>
                                            <Inject services={[Edit, Aggregate]} />
                                            <AggregatesDirective>
                                                <AggregateDirective>
                                                    <AggregateColumnsDirective>
                                                        <AggregateColumnDirective
                                                            footerTemplate={totalRowTemplate}
                                                            // the aggregation type doesn't matter. you just have to supply something so the row will render
                                                            type='Min'
                                                            // by picking the accountCode field, we make sure the template will render in the correct position within the grid
                                                            field='accountCode'
                                                        />
                                                        <AggregateColumnDirective
                                                            type='Sum'
                                                            field='debitTotal'
                                                            format={numberFormatOptions}
                                                        />
                                                        <AggregateColumnDirective
                                                            type='Sum'
                                                            field='creditTotal'
                                                            format={numberFormatOptions}
                                                        />
                                                    </AggregateColumnsDirective>
                                                </AggregateDirective>
                                            </AggregatesDirective>
                                        </GridComponent>
                                    </GridStateContextProvider>
                                </div>
                            </div>
                        )}

                        {props.isLoading && (
                            <LoadingSpinner
                                id='loadingSpinnerEnterAmountsGrid'
                                containerStyle={spinnerClassName}
                                label={t('loadingPleaseWait').toString()}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            appearance='primary'
                            onClick={primaryHandler}
                            disabled={props.saveButtonIsDisabled}
                            className={buttonOverrides}
                        >
                            {confirmLabel}
                        </Button>
                        <DialogTrigger>
                            <Button className={buttonOverrides}>{cancelLabel}</Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default EnterAmountsDialog;
